import { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';

import { InlineCommentManager } from '../services/InlineCommentManager';
import { useAutoSavingService } from './AutoSavingServiceContext';
import { useContentEditorService } from './ContentEditorServiceContext';
import { useMplServiceClient } from './MplServiceClientContext';

/**
 * Exported for unit testing. For production code, please use
 * `useInlineCommentManager()` and `InlineCommentManagerProvider` instead.
 */
export const InlineCommentManagerContext = createContext<InlineCommentManager | undefined>(
  undefined,
);

export function InlineCommentManagerProvider({ children }: { children: ReactNode }) {
  const mpl = useMplServiceClient();
  const autoSaving = useAutoSavingService();
  const contentEditor = useContentEditorService();
  const service = useMemo(
    () => new InlineCommentManager(mpl, autoSaving, contentEditor),
    [mpl, autoSaving, contentEditor],
  );
  useEffect(() => () => service.dispose(), [service]);
  return (
    <InlineCommentManagerContext.Provider value={service}>
      {children}
    </InlineCommentManagerContext.Provider>
  );
}

export function useInlineCommentManager(): InlineCommentManager {
  const context = useContext(InlineCommentManagerContext);
  if (!context) {
    throw new Error('useInlineCommentManager must be used within InlineCommentManagerProvider');
  }
  return context;
}
